import { useEffect, useRef, useState } from "react";
import type { Snapshot, Whisper } from "@earpiece/contracts";
import { api } from "./api.js";
import { acquireWakeLock, BrowserTTSProvider } from "./audio.js";

interface Session { authenticated: boolean; twitchConfigured: boolean; broadcaster?: { id: string; login: string }; snapshot?: Snapshot; }
const emptySnapshot: Snapshot = { connection: "idle", pipelineMode: "rules", scene: "", queue: [], collecting: 0 };

export function App() {
  const [session, setSession] = useState<Session>();
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string>();
  const [overlayUrl, setOverlayUrl] = useState<string>();
  const [scene, setScene] = useState("");
  const tts = useRef(new BrowserTTSProvider());
  const spokenIds = useRef(new Set<string>());
  const sessionStopped = useRef(false);
  const wakeLock = useRef<{ release(): Promise<void> } | undefined>(undefined);

  useEffect(() => { void api<Session>("/api/session").then((value) => { setSession(value); if (value.snapshot) { setSnapshot(value.snapshot); setScene(value.snapshot.scene); } }).catch((cause) => setError(cause.message)); }, []);
  useEffect(() => {
    if (!session?.authenticated) return;
    const events = new EventSource("/api/events", { withCredentials: true });
    events.addEventListener("snapshot", (event) => setSnapshot(JSON.parse((event as MessageEvent).data) as Snapshot));
    events.onerror = () => setError("Realtime connection interrupted; reconnecting automatically.");
    return () => events.close();
  }, [session?.authenticated]);
  useEffect(() => {
    if (!audioUnlocked) return;
    const whisper = snapshot.queue.find((item) => item.status === "queued" && !spokenIds.current.has(item.id));
    if (!whisper) return;
    spokenIds.current.add(whisper.id);
    void speakWhisper(whisper);
  }, [snapshot.queue, audioUnlocked]);
  useEffect(() => {
    const resume = () => { if (document.visibilityState === "visible" && audioUnlocked) void acquireWakeLock().then((lock) => { wakeLock.current = lock; }); };
    document.addEventListener("visibilitychange", resume); return () => document.removeEventListener("visibilitychange", resume);
  }, [audioUnlocked]);

  async function unlockAndStart() {
    try {
      await tts.current.unlock(); wakeLock.current = await acquireWakeLock();
      sessionStopped.current = false; setAudioUnlocked(true); setError(undefined); await api("/api/live/start", { method: "POST" });
    } catch (cause) { setError((cause as Error).message); }
  }
  async function speakWhisper(whisper: Whisper) {
    try {
      await api(`/api/whispers/${whisper.id}/ack`, { method: "POST", body: JSON.stringify({ status: "speaking" }) });
      await tts.current.speak(whisper.brief);
      await api(`/api/whispers/${whisper.id}/ack`, { method: "POST", body: JSON.stringify({ status: "spoken" }) });
    } catch (cause) {
      await api(`/api/whispers/${whisper.id}/ack`, { method: "POST", body: JSON.stringify({ status: "failed" }) }).catch(() => undefined);
      if (!sessionStopped.current) setError((cause as Error).message);
    }
  }
  async function stopSession() {
    if (stopping) return;
    sessionStopped.current = true; setStopping(true); setError(undefined);
    try {
      await api("/api/live/stop", { method: "POST" });
      tts.current.cancel();
      await wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = undefined;
      spokenIds.current.clear();
      setAudioUnlocked(false);
      setSnapshot((current) => ({ ...current, connection: "stopped", queue: [], collecting: 0 }));
    } catch (cause) {
      sessionStopped.current = false;
      setError((cause as Error).message);
    } finally { setStopping(false); }
  }
  async function saveScene() {
    try { await api("/api/scene", { method: "PUT", body: JSON.stringify({ scene }) }); }
    catch (cause) { setError((cause as Error).message); }
  }
  async function rotateOverlay() {
    try { setOverlayUrl((await api<{ url: string }>("/api/overlay-token/rotate", { method: "POST" })).url); }
    catch (cause) { setError((cause as Error).message); }
  }

  if (!session) return <main className="phone"><p>Loading Earpiece…</p></main>;
  if (!session.authenticated) return <main className="phone auth"><div className="brand">EARPIECE</div><h1>Chat, without looking down.</h1><p>Connect the broadcaster account. Tokens remain encrypted on the server.</p>{session.twitchConfigured ? <a className="primary" href="/auth/twitch">Connect Twitch</a> : <p className="error">Twitch server credentials are not configured.</p>}</main>;

  return <main className="phone">
    <header><div><span className={`dot ${snapshot.connection}`} /> {snapshot.connection}</div><div>{snapshot.pipelineMode === "llm" ? "AI" : "RULES"}</div></header>
    <div className="brand">EARPIECE</div><h1>@{session.broadcaster?.login}</h1>
    {!audioUnlocked ? <button className="primary" onClick={() => void unlockAndStart()}>Unlock whispers &amp; go live</button> : <>
      <section className="now"><span>{snapshot.queue[0]?.status ?? "LISTENING"}</span><p>{snapshot.queue[0]?.brief ?? `${snapshot.collecting} messages collecting`}</p></section>
      <p className="hint">Keep this page in the foreground to hear viewer whispers.</p>
    </>}
    {error && <p className="error">{error}</p>}
    <details><summary>Session controls</summary>
      <label>Scene context<textarea value={scene} maxLength={160} onChange={(event) => setScene(event.target.value)} /></label>
      <button onClick={() => void saveScene()}>Save scene</button>
      <button onClick={() => void rotateOverlay()}>Rotate OBS overlay link</button>
      {overlayUrl && <label>Copy this once<input readOnly value={overlayUrl} onFocus={(event) => event.currentTarget.select()} /></label>}
      <button className="danger" disabled={stopping} onClick={() => void stopSession()}>{stopping ? "Stopping…" : "Stop session"}</button>
    </details>
  </main>;
}

